import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Modal } from '@mui/material';
import styled, { keyframes } from 'styled-components';
import { Row } from 'antd';
import Loading from 'components/Loading';
import { useDispatch } from 'react-redux';
import { Close, CloudUpload } from '@mui/icons-material';
import { BirdLogo } from 'utils/assets/images';
import { useCurrentUser } from 'store/users/selectors';
import { handleCreateFeedback, handleRevokeFeedbackForm } from 'store/feedback/thunks';
import { useFeedback, useFeedbackForm } from 'store/feedback/selectors';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { storage } from 'firebaseConfig';
import { ref as fireRef, uploadBytes, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { v4 } from 'uuid';
import dayjs from 'utils/dayjsConfig';
import AudioRecorder from 'containers/chat/AudioRecorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LogoIcon from 'components/LogoIcon';
import { COLOR } from 'utils/constants/color';

const FeedBackWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background: rgba(9, 11, 18, 0.5);
    backdrop-filter: blur(2px);
`;

const BirdLogoImg = styled.div`
    width: 120px;
    height: 52px;
    background-image: url(${BirdLogo});
    background-position: center;
    background-size: contain;
    background-repeat: no-repeat;
`;

const CloseBtn = styled.div`
    background-color: rgba(255, 255, 255, 0.13);
    border: 1px solid rgba(255, 255, 255, 0.2);
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    position: absolute;
    top: 22px;
    right: 24px;
    color: white;
    z-index: 1;
    transition: all 220ms ease;

    &:hover {
        background-color: rgba(255, 255, 255, 0.22);
    }
`;

const FeedbackTitle = styled.div`
    color: white;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 6px;
    text-align: center;
    white-space: pre-line;
    line-height: 1.45;
`;

const Input = styled.textarea`
    background-color: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    box-shadow: none;
    font-size: 14px;
    font-weight: 500;
    outline: none;
    padding: 12px;
    transition: all 300ms ease;
    width:100%;
    min-height: 82px;
    text-align:left;
    border-radius: 10px;
    resize: vertical;

    &::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }

    &:focus {
        border-color: rgba(255, 255, 255, 0.5);
        background-color: rgba(255, 255, 255, 0.06);
    }
`;

const EmailInput = styled.input`
    background-color: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    box-shadow: none;
    font-size: 14px;
    font-weight: 500;
    outline: none;
    padding: 8px 12px;
    transition: all 300ms ease;
    width: 100%;
    height: 40px;
    text-align: left;
    border-radius: 10px;
    &::placeholder {
        color: rgba(255, 255, 255, 0.6);
    }
    
    &:invalid {
        border-color: rgba(255, 0, 0, 0.7);
    }

    &:focus {
        border-color: rgba(255, 255, 255, 0.5);
        background-color: rgba(255, 255, 255, 0.06);
    }
`;

const FeedbackSendBtn = styled.button`
    background-image: linear-gradient(to right, rgb(189,38,95), rgb(217,87,61));
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 220px;
    height: 46px;
    color: white;
    z-index: 1;
    padding: 5px;
    border-radius: 12px;
    transition: all 200ms ease;

    &:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.28);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
`;

const FeedbackInner = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.65);
    border-radius: 12px;
`;

const FeedbackThnks = styled.div`
    width:100%;
    min-height: 180px;
    background: linear-gradient(135deg, rgba(189, 38, 95, 0.95) 0%, rgba(217, 87, 61, 0.96) 100%);
    color: white;
    z-index: 1;
    white-space: pre-line;
    padding: 22px;
    text-align: center;
    margin-bottom: 8px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1.5;
    font-size: 15px;
    letter-spacing: 0.2px;
`;

const FeedbackContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 50%;
    max-width: 94vw;
    width: 760px;
    max-height: 90vh;
    background: radial-gradient(circle at top, rgb(67, 71, 84) 0%, rgb(35, 38, 48) 58%, rgb(29, 31, 40) 100%);
    position: relative;
    justify-content: flex-start;
    padding: 24px;
    overflow-y: auto;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
    
    &::-webkit-scrollbar {
        width: 9px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.12);
        border-radius: 8px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(120, 152, 255, 0.95);
        border-radius: 8px;
        border: 2px solid rgba(20, 24, 37, 0.8);
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(152, 177, 255, 1);
    }
`;

const HeaderRow = styled(Row)`
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    min-height: 64px;
    padding: 6px 0 12px;
    margin-bottom: 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const HeaderTitle = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    color: #fff;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-align: center;
`;

const SectionRow = styled(Row)`
    justify-content: center;
    padding-left: 12px;
    padding-right: 12px;
    width: 100%;
`;

const TitleRow = styled(SectionRow)`
    margin-top: 0;
    margin-bottom: 6px;
`;

const EmailRow = styled(SectionRow)`
    padding-top: 10px;
`;

const TextareaRow = styled(SectionRow)`
    padding-top: 10px;
`;

const UploadSection = styled(SectionRow)`
    padding-top: 10px;
    flex-direction: column;
    gap: 10px;
`;

const UploadControls = styled(Row)`
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    gap: 10px;
    flex-wrap: wrap;
    margin-top: 4px;
    margin-bottom: 7px;
`;

const EmailFieldWrap = styled.div`
    width: 100%;
`;

const EmailErrorText = styled.div`
    color: rgba(255, 120, 120, 0.98);
    font-size: 12px;
    margin-top: 4px;
    padding-left: 2px;
`;

const AudioRecorderWrap = styled.div`
    position: relative;
`;

const ActionRow = styled(SectionRow)`
    padding-top: 12px;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding-bottom: 4px;
`;

const FormCard = styled.div`
    width: 100%;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 12px;
    padding: 10px;
`;

const FieldLabel = styled.div`
    color: rgba(255, 255, 255, 0.95);
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
    letter-spacing: 0.2px;
`;

const UploadHeader = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 12px;
    font-weight: 500;
`;

const UploadCount = styled.span`
    color: rgba(255, 255, 255, 0.65);
    font-size: 11px;
`;

const EmptyScreenshotsState = styled.div`
    width: 100%;
    min-height: 74px;
    border: 1px dashed rgba(255, 255, 255, 0.25);
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 14px;
`;

const ScreenshotBtn = styled.button`
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid #fff;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 8px 16px;
    color: white;
    font-size: 12px;
    border-radius: 4px;
    transition: all 300ms ease;
    
    &:hover:not(:disabled) {
        background-color: rgba(255, 255, 255, 0.2);
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const FileUploadBtn = styled.button`
    background-color: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.55);
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 7px 14px;
    color: white;
    font-size: 13px;
    border-radius: 8px;
    transition: all 300ms ease;
    min-height: 34px;
    
    &:hover:not(:disabled) {
        background-color: rgba(255, 255, 255, 0.26);
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ScreenshotsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(74px, 1fr));
    gap: 8px;
    width: 100%;
    margin-top: 2px;
    min-height: 74px;
    max-height: 160px;
    overflow-y: auto;
    
    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.12);
        border-radius: 8px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: rgba(120, 152, 255, 0.95);
        border-radius: 8px;
        border: 2px solid rgba(20, 24, 37, 0.75);
    }
    
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(152, 177, 255, 1);
    }
`;

const ButtonLinkLogo = styled.button`
    align-items: center;
    background-color: transparent;
    border: none;
    cursor: pointer;
    display: inline-flex;
    flex-direction: row;
    font-size: 14px;
    outline: none;
    color: white;
    margin: 0px 0px 2px;
    @media (max-width: 568px) {
        padding: 0px 2px;
        font-size: 12px;
        &:hover {
 
        }
    }
    @media (max-width: 320px) {
        padding: 4px 1px;
        font-size: 10px;
    }
`;

const ScreenshotItem = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    border-radius: 10px;
    overflow: hidden;
    background-color: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const RemoveBtn = styled.div`
    position: absolute;
    top: 5px;
    right: 5px;
    background-color: rgba(204, 80, 71, 0.9);
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: white;
    transition: all 300ms ease;
    
    &:hover {
        background-color: rgb(204, 80, 71);
    }
`;

const ProgressOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: 600;
`;

const ScreenshotOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 9999;
`;

const CornerIndicator = styled.div`
    position: fixed;
    width: 50px;
    height: 50px;
    border: 6px solid #ff0000;
    background-color: rgba(255, 0, 0, 0.2);
    box-shadow: 0 0 20px rgba(255, 0, 0, 0.8), 0 0 40px rgba(255, 0, 0, 0.6);
    animation: blink 0.5s infinite;
    
    @keyframes blink {
        0%, 100% {
            opacity: 1;
            transform: scale(1);
        }
        50% {
            opacity: 0.4;
            transform: scale(1.1);
        }
    }
    
    &.top-left {
        top: 10px;
        left: 10px;
        border-right: none;
        border-bottom: none;
        border-top-left-radius: 8px;
    }
    
    &.top-right {
        top: 10px;
        right: 10px;
        border-left: none;
        border-bottom: none;
        border-top-right-radius: 8px;
    }
    
    &.bottom-left {
        bottom: 10px;
        left: 10px;
        border-right: none;
        border-top: none;
        border-bottom-left-radius: 8px;
    }
    
    &.bottom-right {
        bottom: 10px;
        right: 10px;
        border-left: none;
        border-top: none;
        border-bottom-right-radius: 8px;
    }
`;

const gatheringTextPulse = keyframes`
    0%, 100% {
        opacity: 0.78;
    }
    50% {
        opacity: 1;
    }
`;

const gatheringDotWave = keyframes`
    0%, 80%, 100% {
        opacity: 0.3;
        transform: translateY(0);
    }
    40% {
        opacity: 1;
        transform: translateY(-2px);
    }
`;

const gatheringTrackShimmer = keyframes`
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
`;

const GatheringStatusWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    width: 100%;
`;

const GatheringMessageLine = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 2px;
    color: rgba(255, 255, 255, 0.96);
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    line-height: 1.4;
    animation: ${gatheringTextPulse} 2.2s ease-in-out infinite;
`;

const GatheringDots = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    margin-left: 2px;

    span {
        display: inline-block;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.95);
        animation: ${gatheringDotWave} 1.05s ease-in-out infinite;
    }

    span:nth-child(1) {
        animation-delay: 0ms;
    }

    span:nth-child(2) {
        animation-delay: 150ms;
    }

    span:nth-child(3) {
        animation-delay: 300ms;
    }
`;

const GatheringTrack = styled.div`
    width: 100%;
    max-width: 220px;
    height: 3px;
    border-radius: 999px;
    background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.06) 0%,
        rgba(255, 255, 255, 0.06) 38%,
        rgba(255, 255, 255, 0.55) 50%,
        rgba(255, 255, 255, 0.06) 62%,
        rgba(255, 255, 255, 0.06) 100%
    );
    background-size: 200% 100%;
    animation: ${gatheringTrackShimmer} 1.5s linear infinite;
`;

const FeedbackForm = ({ }) => {
    const feedbackForm = useFeedbackForm()
    const feedback = useFeedback()
    const dispatch = useDispatch();
    const showAlert = useAlert();
    const currentUser = useCurrentUser()
    const [feedbackLoad, setFeedbackLoad] = useState(false)
    const [feedBackData, setFeedBackData] = useState({ content: "" });
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [feedbackSubmission, setFeedbackSubmission] = useState(false);
    const [screenshotUrls, setScreenshotUrls] = useState([]);
    const [capturingScreenshot, setCapturingScreenshot] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [audioUrl, setAudioUrl] = useState(null);
    const fileInputRef = useRef(null);
    const autoCapturedRef = useRef(false);
    const autoSubmitTriggered = useRef(false);
    const consoleErrorsRef = useRef([]);
    const errorHandlersRef = useRef([]);
    const revokeFeedback = useCallback(() => {
        dispatch(handleRevokeFeedbackForm())
    }, [dispatch])

    const getSystemInfo = async () => {
        let ipAddress = 'N/A';
        try {
            const response = await fetch('https://api.ipify.org?format=json');
            const data = await response.json();
            ipAddress = data.ip || 'N/A';
        } catch (error) {
            console.error('Error fetching IP address:', error);
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                ipAddress = data.ip || 'N/A';
            } catch (e) {
                console.error('Error fetching IP from backup service:', e);
            }
        }

        return {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            referrer: document.referrer || 'direct',
            ipAddress: ipAddress
        };
    };

    const getCookies = () => {
        const cookies = {};
        if (document.cookie) {
            document.cookie.split(';').forEach(cookie => {
                const [name, value] = cookie.trim().split('=');
                if (name) {
                    cookies[name] = value || '';
                }
            });
        }
        return cookies;
    };

    const getLocalStorage = () => {
        const storage = {};
        try {
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key) {
                    storage[key] = localStorage.getItem(key);
                }
            }
        } catch (error) {
            console.error('Error reading localStorage:', error);
        }
        return storage;
    };

    const getSessionStorage = () => {
        const storage = {};
        try {
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key) {
                    storage[key] = sessionStorage.getItem(key);
                }
            }
        } catch (error) {
            console.error('Error reading sessionStorage:', error);
        }
        return storage;
    };

    const collectSystemData = () => {
        return {
            cookies: getCookies(),
            consoleErrors: consoleErrorsRef.current,
            localStorage: getLocalStorage(),
            sessionStorage: getSessionStorage(),
            timestamp: new Date().toISOString()
        };
    };

    const uploadSystemDataFile = async () => {
        await Promise.resolve();
        try {
            const systemData = collectSystemData();
            const jsonString = JSON.stringify(systemData, null, 2);
            const blob = new Blob([jsonString], { type: 'application/json' });

            const location = '/feedback/system-data';
            const id = `${dayjs().format('YYYY-MM-DD')}-${v4()}.json`;
            const fileRef = fireRef(storage, `${location}/${id}`);

            await uploadBytes(fileRef, blob);
            const url = await getDownloadURL(fileRef);
            return url;
        } catch (error) {
            console.error('Error uploading system data file:', error);
            return null;
        }
    };

    const captureScreenshot = async () => {
        try {
            setCapturingScreenshot(true);
            showAlert('Screen capturing...', 'info');
            const html2canvas = (await import('html2canvas')).default;
            const modalElement = document.querySelector('[role="presentation"]');
            const originalDisplay = modalElement ? modalElement.style.display : null;

            if (modalElement) {
                modalElement.style.display = 'none';
            }

            await new Promise(resolve => setTimeout(resolve, 50));

            const body = document.body;
            const html = document.documentElement;
            const fullHeight = Math.max(
                body.scrollHeight,
                body.offsetHeight,
                html.clientHeight,
                html.scrollHeight,
                html.offsetHeight
            );
            const fullWidth = Math.max(
                body.scrollWidth,
                body.offsetWidth,
                html.clientWidth,
                html.scrollWidth,
                html.offsetWidth
            );

            const canvas = await html2canvas(document.body, {
                height: fullHeight,
                width: fullWidth,
                useCORS: true,
                logging: false,
                scale: 0.5,
                windowWidth: fullWidth,
                windowHeight: fullHeight,
                allowTaint: false,
                removeContainer: true,
                backgroundColor: null,
                scrollX: 0,
                scrollY: 0,
                ignoreElements: (element) => {
                    return element.closest('[role="presentation"]') !== null;
                }
            });

            if (modalElement) {
                modalElement.style.display = originalDisplay || '';
            }

            return new Promise((resolve, reject) => {
                canvas.toBlob(async (blob) => {
                    if (blob) {
                        const location = '/feedback/screenshots';
                        const id = `${dayjs().format('YYYY-MM-DD')}-${v4()}.png`;
                        const fileRef = fireRef(storage, `${location}/${id}`);

                        try {
                            await uploadBytes(fileRef, blob);
                            const url = await getDownloadURL(fileRef);
                            setCapturingScreenshot(false);
                            resolve(url);
                        } catch (error) {
                            console.error('Screenshot upload error:', error);
                            setCapturingScreenshot(false);
                            reject(error);
                        }
                    } else {
                        setCapturingScreenshot(false);
                        resolve(null);
                    }
                }, 'image/png', 0.8);
            });
        } catch (error) {
            console.error('Screenshot capture error:', error);
            setCapturingScreenshot(false);

            const modalElement = document.querySelector('[role="presentation"]');
            if (modalElement && modalElement.style.display === 'none') {
                modalElement.style.display = '';
            }

            return null;
        }
    };

    const submitFeedback = useCallback(async () => {
        if (!validateEmail(email)) {
            showAlert('Please enter a valid email address', 'error');
            return;
        }

        setFeedbackLoad(true);

        const [
            screenshotResult,
            systemInfoResult,
            systemDataResult,
        ] = await Promise.allSettled([
            captureScreenshot(),
            getSystemInfo(),
            uploadSystemDataFile(),
        ]);

        const autoScreenshotUrl =
            screenshotResult.status === 'fulfilled' ? screenshotResult.value : null;
        if (screenshotResult.status === 'rejected') {
            console.error('Failed to capture screenshot:', screenshotResult.reason);
        }

        const systemInfo =
            systemInfoResult.status === 'fulfilled' && systemInfoResult.value
                ? systemInfoResult.value
                : await getSystemInfo();

        const systemDataUrl =
            systemDataResult.status === 'fulfilled' ? systemDataResult.value : null;
        if (systemDataResult.status === 'rejected') {
            console.error('Failed to upload system data:', systemDataResult.reason);
        }
        const allScreenshotUrls = [...screenshotUrls];
        if (autoScreenshotUrl) {
            allScreenshotUrls.push({ url: autoScreenshotUrl, type: 'auto', id: v4() });
        }

        const screenshotUrlArray = allScreenshotUrls.map(item => item.url);
        const data = {
            ...feedBackData,
            email: email.trim(),
            userId: currentUser?.id,
            userRole: currentUser?.role,
            username: currentUser?.username,
            name: `${currentUser?.firstName ?? ''} ${currentUser?.lastName ?? ''}`,
            screenshotUrl: screenshotUrlArray.length > 0 ? JSON.stringify(screenshotUrlArray) : null,
            audioUrl: audioUrl || null,
            systemInfo: JSON.stringify(systemInfo),
            systemDataUrl: systemDataUrl || null,
            orgId: currentUser?.orgId || null,
            status: 'Not started'
        };

        dispatch(handleCreateFeedback(data, () => {
            setFeedbackSubmission(true);
            setFeedBackData({ content: "" });
            setEmail("");
            setEmailError("");
            setScreenshotUrls([]);
            setAudioUrl(null);
            consoleErrorsRef.current = [];
            setTimeout(() => {
                setFeedbackSubmission(false);
                dispatch(handleRevokeFeedbackForm());
                setFeedbackLoad(false);
            }, 4000);
        }));
    }, [feedBackData, screenshotUrls, audioUrl, email, currentUser, dispatch, showAlert]);

    useEffect(() => {
        if (!feedbackForm.isLoading) {
            setScreenshotUrls([]);
            setAudioUrl(null);
            setEmail("");
            setEmailError("");
            autoCapturedRef.current = false;
            autoSubmitTriggered.current = false;
            consoleErrorsRef.current = [];

            errorHandlersRef.current.forEach(handler => {
                if (handler.remove) {
                    handler.remove();
                }
            });
            errorHandlersRef.current = [];
        } else if (feedbackForm.isLoading) {
            const errorHandler = (event) => {
                consoleErrorsRef.current.push({
                    message: event.message || 'Unknown error',
                    source: event.filename || 'Unknown',
                    lineno: event.lineno || 0,
                    colno: event.colno || 0,
                    error: event.error ? {
                        name: event.error.name,
                        message: event.error.message,
                        stack: event.error.stack
                    } : null,
                    timestamp: new Date().toISOString()
                });
            };

            const unhandledRejectionHandler = (event) => {
                consoleErrorsRef.current.push({
                    message: event.reason?.message || 'Unhandled Promise Rejection',
                    source: 'Promise',
                    error: {
                        name: event.reason?.name || 'UnhandledRejection',
                        message: event.reason?.message || String(event.reason),
                        stack: event.reason?.stack || null
                    },
                    timestamp: new Date().toISOString()
                });
            };

            window.addEventListener('error', errorHandler);
            window.addEventListener('unhandledrejection', unhandledRejectionHandler);

            errorHandlersRef.current = [
                { remove: () => window.removeEventListener('error', errorHandler) },
                { remove: () => window.removeEventListener('unhandledrejection', unhandledRejectionHandler) }
            ];
        }
    }, [feedbackForm.isLoading]);

    useEffect(() => {
        if (feedbackForm.isLoading && feedbackForm.autoSubmit && feedbackForm.teacherDescription && !autoSubmitTriggered.current) {
            const teacherContent = `Teacher (${feedbackForm.teacherName || 'Teacher'}): ${feedbackForm.teacherDescription}`;
            setFeedBackData({ content: teacherContent });
            if (feedbackForm.audioUrl) {
                setAudioUrl(feedbackForm.audioUrl);
            }
            if (feedbackForm.email) {
                setEmail(feedbackForm.email);
            }
            autoSubmitTriggered.current = true;
        }
    }, [feedbackForm.isLoading, feedbackForm.autoSubmit, feedbackForm.teacherDescription, feedbackForm.teacherName, feedbackForm.audioUrl]);

    useEffect(() => {
        if (feedbackForm.autoSubmit && autoSubmitTriggered.current && feedBackData.content && feedBackData.content.includes('Teacher (')) {
            const timeoutId = setTimeout(() => {
                submitFeedback();
            }, 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [feedBackData.content, feedbackForm.autoSubmit, submitFeedback]);

    const handleFileUpload = async (files) => {
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files);
        const maxSize = 10 * 1024 * 1024;
        const maxImages = 5;
        const currentImageCount = screenshotUrls.length;
        const remainingSlots = maxImages - currentImageCount;

        if (remainingSlots <= 0) {
            showAlert(`Maximum ${maxImages} images allowed. Please remove some images first.`, 'error');
            return;
        }

        const filesToUpload = fileArray.slice(0, remainingSlots);
        if (fileArray.length > remainingSlots) {
            showAlert(`Only ${remainingSlots} more image(s) can be uploaded. ${fileArray.length - remainingSlots} file(s) were skipped.`, 'warning');
        }

        setUploadingFiles(true);

        const uploadPromises = [];

        for (let i = 0; i < filesToUpload.length; i++) {
            const file = filesToUpload[i];

            if (file.size > maxSize) {
                showAlert(`File ${file.name} exceeds 10MB limit. Skipping...`, 'error');
                continue;
            }

            if (!file.type.startsWith('image/')) {
                showAlert(`File ${file.name} is not an image. Skipping...`, 'error');
                continue;
            }

            const fileId = v4();
            const location = '/feedback/screenshots';
            const id = `${dayjs().format('YYYY-MM-DD')}-${fileId}-${file.name}`;
            const fileRef = fireRef(storage, `${location}/${id}`);

            const uploadPromise = new Promise((resolve, reject) => {
                try {
                    setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));

                    const uploadTask = uploadBytesResumable(fileRef, file);

                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                            setUploadProgress(prev => ({ ...prev, [fileId]: progress }));
                        },
                        (error) => {
                            console.error('Upload error:', error);
                            setUploadProgress(prev => {
                                const newProgress = { ...prev };
                                delete newProgress[fileId];
                                return newProgress;
                            });
                            reject(error);
                        },
                        async () => {
                            try {
                                const url = await getDownloadURL(uploadTask.snapshot.ref);
                                setScreenshotUrls(prev => [...prev, { url, type: 'upload', id: fileId, name: file.name }]);
                                setUploadProgress(prev => {
                                    const newProgress = { ...prev };
                                    delete newProgress[fileId];
                                    return newProgress;
                                });
                                resolve();
                            } catch (error) {
                                console.error('Error getting download URL:', error);
                                reject(error);
                            }
                        }
                    );
                } catch (error) {
                    console.error('Error uploading file:', error);
                    reject(error);
                }
            });

            uploadPromises.push(uploadPromise);
        }

        try {
            await Promise.allSettled(uploadPromises);
        } catch (error) {
            console.error('Error in upload batch:', error);
        } finally {
            setUploadingFiles(false);
        }
    };

    const removeScreenshot = (id) => {
        setScreenshotUrls(prev => prev.filter(item => item.id !== id));
    };

    const validateEmail = (emailValue) => {
        if (emailValue.length > 3) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailValue || emailValue.trim() === '') {
                setEmailError('Email is required');
                return false;
            }
            if (!emailRegex.test(emailValue)) {
                setEmailError('Please enter a valid email address');
                return false;
            }
            setEmailError('');
            return true;
        }
    };

    const handleEmailChange = (e) => {
        const emailValue = e.target.value;
        setEmail(emailValue);
        if (emailError) {
            validateEmail(emailValue);
        }
    };

    const isGatheringFeedbackInfo = capturingScreenshot || feedbackLoad || feedback.isLoading;

    return (
        <>
            {capturingScreenshot && (
                <ScreenshotOverlay>
                    <CornerIndicator className="top-left" />
                    <CornerIndicator className="top-right" />
                    <CornerIndicator className="bottom-left" />
                    <CornerIndicator className="bottom-right" />
                </ScreenshotOverlay>
            )}
            <Modal
                open={feedbackForm.isLoading}
                onClose={revokeFeedback}
                style={{ height: '100%', width: '100%', backgroundColor: "transparent" }}
            >
                <FeedBackWrapper>
                    <FeedbackContainer>
                        <HeaderRow>
                            <ButtonLinkLogo>
                                <LogoIcon height={56} width={50} />
                            </ButtonLinkLogo>
                            <HeaderTitle>Report an issue</HeaderTitle>
                            <CloseBtn onClick={revokeFeedback}>
                                <Close style={{ color: "#fff", fontWeight: "bolder", fontSize: 35 }} />
                            </CloseBtn>
                        </HeaderRow>
                        {!feedbackSubmission ? (<>
                            
                            {isGatheringFeedbackInfo ? (
                                    <GatheringStatusWrap role="status" aria-live="polite">
                                        <GatheringMessageLine>
                                            Gathering information. Please wait
                                            <GatheringDots aria-hidden="true">
                                                <span />
                                                <span />
                                                <span />
                                            </GatheringDots>
                                        </GatheringMessageLine>
                                        <GatheringTrack aria-hidden="true" />
                                    </GatheringStatusWrap>
                                ) : null}
                            <EmailRow>
                                <FormCard>
                                    <FieldLabel>Email address *</FieldLabel>
                                    <EmailFieldWrap>
                                        <EmailInput
                                            type="email"
                                            placeholder="Enter your email *"
                                            value={email}
                                            onChange={handleEmailChange}
                                            onBlur={() => validateEmail(email)}
                                            disabled={feedbackForm.autoSubmit || feedbackLoad}
                                            required
                                        />
                                        {emailError && (
                                            <EmailErrorText>
                                                {emailError}
                                            </EmailErrorText>
                                        )}
                                    </EmailFieldWrap>
                                </FormCard>
                            </EmailRow>
                            <TextareaRow>
                                <FormCard>
                                    <FieldLabel>Issue details *</FieldLabel>
                                    <Input
                                        placeholder="Describe what happened, where it happened, and what you expected."
                                        value={feedBackData.content}
                                        onChange={(e) => setFeedBackData({ ...feedBackData, content: e.target.value })}
                                        disabled={feedbackForm.autoSubmit}
                                        required
                                    />
                                </FormCard>
                            </TextareaRow>
                            <UploadSection>
                                <FormCard>
                                    <UploadHeader>
                                        <span>Attachments (optional)</span>
                                        <UploadCount>{screenshotUrls.length}/5 screenshots</UploadCount>
                                    </UploadHeader>
                                    <UploadControls>
                                        <AudioRecorderWrap>
                                            <AudioRecorder
                                                onRecorderDone={(url) => setAudioUrl(url)}
                                                bottom="60px"
                                                storageLoc="/feedback/audio"
                                                is_report_issue={true}
                                            />
                                        </AudioRecorderWrap>
                                        {audioUrl && (
                                            <div className="audioPlayerFeedback">
                                                <audio
                                                    controls
                                                    src={audioUrl}
                                                    style={{ width: '100%', height: '32px' }}
                                                />
                                                <DeleteOutlineIcon className="audioPlayerDeedbackDeleteIcon" onClick={() => setAudioUrl(null)} />
                                            </div>
                                        )}
                                        <FileUploadBtn onClick={() => fileInputRef.current?.click()} disabled={uploadingFiles || feedbackLoad || capturingScreenshot || screenshotUrls.length >= 5}>
                                            {uploadingFiles ? (
                                                <>
                                                    <Loading size={18} style={{ marginRight: 5 }} />
                                                    Uploading...
                                                </>
                                            ) : (
                                                <>
                                                    <CloudUpload style={{ fontSize: 18, marginRight: 5 }} />
                                                    Add screenshots
                                                </>
                                            )}
                                        </FileUploadBtn>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                if (e.target.files) {
                                                    handleFileUpload(e.target.files);
                                                }
                                                e.target.value = '';
                                            }}
                                        />
                                    </UploadControls>
                                    {screenshotUrls.length > 0 ? (
                                        <ScreenshotsGrid>
                                            {screenshotUrls.map((item) => (
                                                <ScreenshotItem key={item.id}>
                                                    <img src={item.url} alt={item.name || 'Screenshot'} />
                                                    <RemoveBtn onClick={() => removeScreenshot(item.id)}>
                                                        <Close style={{ fontSize: 14 }} />
                                                    </RemoveBtn>
                                                    {uploadProgress[item.id] !== undefined && uploadProgress[item.id] < 100 && (
                                                        <ProgressOverlay>
                                                            <div>{uploadProgress[item.id]}%</div>
                                                        </ProgressOverlay>
                                                    )}
                                                </ScreenshotItem>
                                            ))}
                                        </ScreenshotsGrid>
                                    ) : (
                                        <EmptyScreenshotsState>
                                            Add screenshots to help us reproduce the issue faster.
                                        </EmptyScreenshotsState>
                                    )}
                                </FormCard>
                            </UploadSection>
                            <ActionRow>
                                <FeedbackSendBtn onClick={submitFeedback} disabled={feedBackData.content.length == 0 || !email || emailError || feedbackLoad || capturingScreenshot}>
                                    <FeedbackInner>
                                        {isGatheringFeedbackInfo ? (
                                            <Loading color={"#fff"} size={20} />
                                        ) : (
                                            "Send"
                                        )}
                                    </FeedbackInner>
                                </FeedbackSendBtn>
                            </ActionRow>
                        </>) : (<>
                            <TitleRow style={{ marginTop: 12 }}>
                                <FeedbackThnks>
                                    <FeedbackInner>
                                        {`Thank you for sharing your feedback.\nOur team is reviewing it and will work on it right away.`}
                                    </FeedbackInner>
                                </FeedbackThnks>
                            </TitleRow>
                        </>)}
                    </FeedbackContainer>
                </FeedBackWrapper>
            </Modal>
        </>
    )
}

export default FeedbackForm;